import { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Copy, Check, Instagram, Play } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VIDEO_URL = `${BACKEND_URL}/api/media/demo/social_share.mp4`;
const POSTER_URL = `${BACKEND_URL}/api/media/demo/social_poster.jpg`;

/**
 * <SocialShareVideo />
 *
 * A ready-to-share 30-second vertical (9:16) MP4 with baked-in captions and
 * narration. Optimized for TikTok, Instagram Reels, Facebook Reels, and
 * WhatsApp Status — all four platforms accept 1080x1920 MP4 up to 30s.
 */
export default function SocialShareVideo() {
  const [copied, setCopied] = useState(false);

  const shareCaption = 'Save with your circle. Take your turn receiving the pot. Susu Money is community banking done right. #WorldwideSusu';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(VIDEO_URL);
      setCopied(true);
      toast.success('Video link copied — paste anywhere');
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error('Copy failed — long-press to save the video manually');
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(shareCaption);
      toast.success('Caption copied — paste it under your post');
    } catch (e) { /* clipboard blocked */ }
  };

  return (
    <section id="social-share" className="py-20 px-6 relative overflow-hidden" data-testid="social-share-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-lime-500/8 blur-[140px] rounded-full"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-lime-500/10 border border-lime-500/30 text-lime-400 text-xs font-bold uppercase tracking-widest mb-4">
            <Instagram className="w-3 h-3" />
            Ready for TikTok · Reels · WhatsApp
          </div>
          <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tight leading-tight" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Spread the word.<br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-cyan-300">One video, all platforms.</span>
          </h2>
          <p className="text-neutral-400 mt-3 max-w-xl mx-auto">
            30 seconds. 9:16 vertical. Captions baked in. Download it, then post it anywhere.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          {/* Left: the vertical video preview (constrained height) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mx-auto"
          >
            <div className="relative aspect-[9/16] w-full max-w-[280px] rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_60px_rgba(132,204,22,0.15)] bg-black">
              <video
                src={VIDEO_URL}
                poster={POSTER_URL}
                controls
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                data-testid="social-share-video"
              />
              <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 backdrop-blur-md border border-white/10 text-[10px] font-bold text-white uppercase tracking-wider">
                <Play className="w-3 h-3" fill="currentColor" />
                30 sec · 9:16
              </div>
            </div>
          </motion.div>

          {/* Right: how to share + download/copy CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-4"
          >
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
              <div className="text-[11px] uppercase tracking-widest text-lime-400 font-bold mb-2">Suggested caption</div>
              <div className="text-white text-base leading-relaxed">{shareCaption}</div>
              <button
                onClick={copyCaption}
                className="mt-3 text-xs font-bold text-neutral-400 hover:text-white flex items-center gap-1 transition-colors"
                data-testid="social-share-copy-caption"
              >
                <Copy className="w-3 h-3" /> Copy caption
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={VIDEO_URL}
                download="worldwide-susu-30s.mp4"
                className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-lime-500 hover:bg-lime-400 text-black font-black transition-transform hover:scale-105"
                data-testid="social-share-download"
              >
                <Download className="w-5 h-5" />
                Download MP4
              </a>
              <button
                onClick={copyLink}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold transition-colors"
                data-testid="social-share-copy-link"
              >
                {copied ? <Check className="w-5 h-5 text-lime-400" /> : <Copy className="w-5 h-5" />}
                {copied ? 'Copied!' : 'Copy link'}
              </button>
            </div>

            <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 text-xs text-neutral-400 leading-relaxed">
              <div className="font-bold text-white mb-2 text-sm">Where to post it</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                <div>• TikTok — 30s hits the sweet spot</div>
                <div>• Instagram Reels</div>
                <div>• Facebook Reels</div>
                <div>• WhatsApp Status (30s max ✓)</div>
                <div>• YouTube Shorts</div>
                <div>• LinkedIn video post</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
